import * as d3 from 'd3'
import React, { useState, useEffect } from 'react'
import { OverlayTrigger, Popover } from 'react-bootstrap'

import authorization from 'live/services/authorization'
import i18n from 'live/services/i18n'

import { ZoomHandler } from '../../../../../generic'
import { IHazard } from '../../../../../types'
import { d3PromiseSelect } from '../../../../../utils'
import {
    topEventRadius,
    initialThreat,
    initialConsequence,
    threatConsequenceHeight,
    barrierHeight,
    hazardBoxWidth,
    shadowOpacity,
    settingsZoomLevel
    // THREAT_COSNEQUENCE_GAP_X,
    // THREAT_CONSEQUENCE_GAP_Y,
    // THRAT_CONSEQUENCE_START_CURVE_GAP
} from '../../../../../utils/constants'
import { IDiagramConsequence, IDiagramThreat, IDiagramTypes } from '../../RiskDiagramEditor'
import { ThreatAndConsequenceEditor, AddThreatConsequence, MiddlePulse, TopBar } from './components'
import { ShowFrequencyButton } from './styles'

interface Props {
    height: number
    width: number
    threats: IDiagramThreat[]
    consequences: IDiagramConsequence[]
    topEvent: string
    hazard: IHazard
    onHazardClick: () => void
    onTopEventClick: () => void
    onThreatConsequenceClick: (type: 'THREAT' | 'CONSEQUENCE', item: IDiagramThreat | IDiagramConsequence) => void
    onAddButtonClick: (type: 'THREAT' | 'CONSEQUENCE') => void
    onDeleteClick: (type: 'THREAT' | 'CONSEQUENCE', index: number) => void
    editing: {
        type: IDiagramTypes
        index: number
    }
}
export const DiagramEditorSvg: React.FC<Props> = ({
    height,
    width,
    consequences,
    hazard,
    threats,
    topEvent,
    onAddButtonClick,
    onHazardClick,
    onThreatConsequenceClick,
    onTopEventClick,
    onDeleteClick,
    editing
}) => {
    const [vector3, setVector3] = useState<{ x: number; y: number; k: number }>({
        x: width / 2,
        y: height / 2 + 30,
        k: 1
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [bowtieSVG, setBowtieSVG] = useState<d3.Selection<SVGSVGElement, unknown, HTMLElement, any>>(null)
    const [zoomBehaviour, setZoomBehaviour] = useState<d3.ZoomBehavior<Element, unknown>>(null)
    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN
    const [showFrequency, setShowFrequency] = useState<boolean>(false)

    const handleShowFrequency = (): void => {
        setShowFrequency(!showFrequency)
    }

    const buttonHideAndDisplayFrequency = (
        <Popover id="popover-trigger-hover-focus">
            {showFrequency
                ? i18n('Hide frequency on threats and consequences')
                : i18n('Show frequency on threats and consequences')}
        </Popover>
    )

    useEffect(() => {
        const initd3Listeners = async (): Promise<void> => {
            const svg = await d3PromiseSelect<SVGSVGElement, unknown>('#bowtie-svg')

            const zoomHandler = d3
                .zoom()
                .on('zoom', () => setVector3(d3.event.transform))
                .scaleExtent([settingsZoomLevel[0], settingsZoomLevel[1]])

            setTimeout(() => {
                zoomHandler(svg)
                svg.on('dblclick.zoom', null)

                svg.call(zoomHandler.transform, d3.zoomIdentity.translate(width / 2, height / 2 + 30).scale(1))
                setZoomBehaviour(() => zoomHandler)
                setBowtieSVG(svg)
            }, 400)
        }

        initd3Listeners()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const getYPosition = (index: number): number => {
        return (threatConsequenceHeight + barrierHeight * 1.5) * index
    }

    const drawShadow = (): JSX.Element => {
        const centerOfTopEvent = vector3.y * (1 / vector3.k)
        const relativeTopEventRadius = topEventRadius * vector3.k

        return (
            <g transform={`translate(${vector3.x - relativeTopEventRadius}, 0) scale(${vector3.k})`}>
                <svg
                    width={hazardBoxWidth}
                    height={centerOfTopEvent > 0 ? centerOfTopEvent : 0}
                    viewBox={`0 0 ${hazardBoxWidth} ${centerOfTopEvent > 0 ? centerOfTopEvent : 0}`}
                    fill="none"
                    opacity={shadowOpacity}
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d={`M${hazardBoxWidth / 2} ${centerOfTopEvent}L${hazardBoxWidth} 0H0.0205078L${hazardBoxWidth /
                            2} ${centerOfTopEvent}Z`}
                        fill="#DDDEE1"
                    />
                </svg>
            </g>
        )
    }

    return (
        <>
            {/* <DiagramEditorStyledContainer> */}
            <svg id="bowtie-svg" width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
                <defs>
                    <filter id="blur" x="-50" y="-50" width="200" height="200">
                        <feGaussianBlur result="blurOut" in="offOut" stdDeviation="5" />
                    </filter>
                </defs>
                {drawShadow()}
                <g id="bowtie-container" transform={`translate(${vector3.x} ${vector3.y}) scale(${vector3.k})`}>
                    <g
                        transform={`translate(0, ${-(
                            getYPosition(threats.length) -
                            (threatConsequenceHeight + barrierHeight * 1.5)
                        ) / 2})`}
                    >
                        {threats.map((threat, index, arr) => (
                            <ThreatAndConsequenceEditor
                                key={index}
                                type="THREAT"
                                threatConsequenceData={threat}
                                middleY={
                                    (getYPosition(threats.length) - (threatConsequenceHeight + barrierHeight * 1.5)) / 2
                                }
                                x={initialThreat}
                                y={getYPosition(index)}
                                onThreatConsequenceClick={onThreatConsequenceClick}
                                onDeleteClick={onDeleteClick}
                                focus={
                                    editing
                                        ? editing?.type !== 'THREAT' || editing?.index !== index
                                            ? 'unfocus'
                                            : 'focus'
                                        : 'none'
                                }
                                canDelete={arr.length > 1}
                                index={index}
                                showFrequency={showFrequency}
                            />
                        ))}
                        {bowtieAdmin && (
                            <AddThreatConsequence
                                x={initialThreat}
                                y={getYPosition(threats.length)}
                                onAddRequest={onAddButtonClick}
                                type="THREAT"
                                threatConsequenceLength={threats.length}
                            />
                        )}
                    </g>
                    {/* consequence side */}
                    <g
                        transform={`translate(0, ${-(
                            getYPosition(consequences.length) -
                            (threatConsequenceHeight + barrierHeight * 1.5)
                        ) / 2})`}
                    >
                        {consequences.map((consequence, index, arr) => (
                            <ThreatAndConsequenceEditor
                                key={index}
                                type="CONSEQUENCE"
                                threatConsequenceData={consequence}
                                middleY={
                                    (getYPosition(consequences.length) -
                                        (threatConsequenceHeight + barrierHeight * 1.5)) /
                                    2
                                }
                                x={initialConsequence}
                                y={getYPosition(index)}
                                onThreatConsequenceClick={onThreatConsequenceClick}
                                onDeleteClick={onDeleteClick}
                                focus={
                                    editing
                                        ? editing?.type !== 'CONSEQUENCE' || editing?.index !== index
                                            ? 'unfocus'
                                            : 'focus'
                                        : 'none'
                                }
                                canDelete={arr.length > 1}
                                index={index}
                                showFrequency={showFrequency}
                            />
                        ))}
                        {bowtieAdmin && (
                            <AddThreatConsequence
                                x={initialConsequence}
                                y={getYPosition(consequences.length)}
                                onAddRequest={onAddButtonClick}
                                type="CONSEQUENCE"
                                threatConsequenceLength={consequences.length}
                            />
                        )}
                    </g>
                    <MiddlePulse
                        title={topEvent}
                        onMiddlePulseClick={onTopEventClick}
                        focus={editing ? (editing?.type !== 'TOPEVENT' ? 'unfocus' : 'focus') : 'none'}
                    />
                </g>
                <TopBar
                    hazard={hazard?.name}
                    relativeMiddleX={vector3.x}
                    relativeMiddleY={vector3.y}
                    widgetWidth={width}
                    consequencesX={initialConsequence + vector3.x}
                    threatsX={initialThreat - vector3.x}
                    scale={vector3.k}
                    noPreventionMitigation={true}
                    onHazardClick={onHazardClick}
                    focus={editing ? (editing?.type !== 'HAZARD' ? 'unfocus' : 'focus') : 'none'}
                />
            </svg>
            <ZoomHandler scale={vector3.k} zoomBehaviour={zoomBehaviour} target={bowtieSVG} />
            <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={buttonHideAndDisplayFrequency}>
                <ShowFrequencyButton onClick={handleShowFrequency}>
                    {showFrequency ? <i className="fas fa-eye"></i> : <i className="fas fa-eye-slash"></i>}
                </ShowFrequencyButton>
            </OverlayTrigger>
        </>
    )
}
